import { useMemo } from 'react';
import type { MarketTrade } from '../api/useMarketTrades';
import { mergeTradeSources } from '../utils/mergeTrades';

export type TradeSortField = 'time' | 'volume' | 'price';
export type TradeFilterMode = 'all' | 'accounts';

/** The chart's "now" bound advances between renders; this absorbs the drift. */
const LIVE_EDGE_TOLERANCE_MS = 60_000;

const EMPTY_TRADES: MarketTrade[] = [];

/** A window ending at (or near) now still admits live trades. */
function isLiveWindow(timeRange?: [Date, Date] | null): boolean {
    if (!timeRange) return true;
    return timeRange[1].getTime() >= Date.now() - LIVE_EDGE_TOLERANCE_MS;
}

function withinWindow(
    trades: MarketTrade[],
    timeRange?: [Date, Date] | null
): MarketTrade[] {
    if (!timeRange) return trades;
    const from = timeRange[0].getTime();
    const to = timeRange[1].getTime();
    return trades.filter(t => {
        const ts = t.timestamp.getTime();
        return ts >= from && ts <= to;
    });
}

function byHighlightedAccounts(
    trades: MarketTrade[],
    filter: TradeFilterMode,
    addrs: Set<string>
): MarketTrade[] {
    if (filter !== 'accounts' || addrs.size === 0) return trades;
    return trades.filter(t => addrs.has(t.account));
}

function sortKey(field: TradeSortField): (t: MarketTrade) => number {
    if (field === 'volume') return t => t.quoteAmount ?? 0;
    if (field === 'price') return t => t.price;
    return t => t.timestamp.getTime();
}

/** Sources arrive newest-first, so the default order is already correct. */
function isDefaultOrder(field: TradeSortField, asc: boolean): boolean {
    return field === 'time' && !asc;
}

function sortTrades(
    trades: MarketTrade[],
    field: TradeSortField,
    asc: boolean
): MarketTrade[] {
    if (isDefaultOrder(field, asc)) return trades;
    const key = sortKey(field);
    const dir = asc ? 1 : -1;
    return [...trades].sort((a, b) => (key(a) - key(b)) * dir);
}

function totalQuoteVolume(trades: MarketTrade[]): number {
    return trades.reduce((sum, t) => sum + (t.quoteAmount ?? 0), 0);
}

interface FilteringInput {
    liveTrades: MarketTrade[];
    historicalTrades: MarketTrade[];
    selectedTraderTrades?: MarketTrade[];
    timeRange?: [Date, Date] | null;
    filter: TradeFilterMode;
    highlightedAddrs: Set<string>;
    sortField: TradeSortField;
    sortAsc: boolean;
    minVolume: number;
}

export interface FilteringResult {
    /** Inside the window, before any user filter. The denominator for stats. */
    windowTrades: MarketTrade[];
    /** After the account filter, before the volume threshold. */
    accountFilteredTrades: MarketTrade[];
    filteredTrades: MarketTrade[];
    windowVolume: number;
    windowIsLive: boolean;
    /** A window bounds the list, so a ranking or threshold can be honest. */
    canRankByValue: boolean;
    effectiveSortField: TradeSortField;
    effectiveSortAsc: boolean;
    effectiveMinVolume: number;
    controlsActive: boolean;
    activeControlCount: number;
}

/**
 * Builds the rendered trade list from its three sources, neutralising any
 * ranking or threshold that no window bounds — over a cursor-paginated
 * newest-first list those claim something about rows nobody has fetched.
 */
export function useTradeFiltering({
    liveTrades,
    historicalTrades,
    selectedTraderTrades,
    timeRange,
    filter,
    highlightedAddrs,
    sortField,
    sortAsc,
    minVolume,
}: FilteringInput): FilteringResult {
    // Derived, not corrected in an effect: an effect would render one frame of
    // the unwindowed list under the old sort before snapping back.
    const canRankByValue = !!timeRange;
    const effectiveSortField = canRankByValue ? sortField : 'time';
    const effectiveSortAsc = canRankByValue ? sortAsc : false;
    const effectiveMinVolume = canRankByValue ? minVolume : 0;

    const windowIsLive = isLiveWindow(timeRange);

    // Identity-stable, so a historical window stops re-merging on every tick.
    const liveSource = windowIsLive ? liveTrades : EMPTY_TRADES;

    // Live first so it wins dedup; trader rows last, contributing only ids the
    // event-wide window is missing.
    const mergedTrades = useMemo(
        () =>
            mergeTradeSources([
                liveSource,
                historicalTrades,
                selectedTraderTrades ?? EMPTY_TRADES,
            ]),
        [liveSource, historicalTrades, selectedTraderTrades]
    );

    const windowTrades = useMemo(
        () => withinWindow(mergedTrades, timeRange),
        [mergedTrades, timeRange]
    );

    const windowVolume = useMemo(
        () => totalQuoteVolume(windowTrades),
        [windowTrades]
    );

    // Its own stage so callers can tell which filter emptied the list.
    const accountFilteredTrades = useMemo(
        () => byHighlightedAccounts(windowTrades, filter, highlightedAddrs),
        [windowTrades, filter, highlightedAddrs]
    );

    const filteredTrades = useMemo(() => {
        const overThreshold =
            effectiveMinVolume > 0
                ? accountFilteredTrades.filter(
                      t => (t.quoteAmount ?? 0) >= effectiveMinVolume
                  )
                : accountFilteredTrades;
        return sortTrades(overThreshold, effectiveSortField, effectiveSortAsc);
    }, [
        accountFilteredTrades,
        effectiveMinVolume,
        effectiveSortField,
        effectiveSortAsc,
    ]);

    const sortActive = !isDefaultOrder(effectiveSortField, effectiveSortAsc);
    const controlsActive = sortActive || effectiveMinVolume > 0;

    return {
        windowTrades,
        accountFilteredTrades,
        filteredTrades,
        windowVolume,
        windowIsLive,
        canRankByValue,
        effectiveSortField,
        effectiveSortAsc,
        effectiveMinVolume,
        controlsActive,
        activeControlCount:
            (sortActive ? 1 : 0) + (effectiveMinVolume > 0 ? 1 : 0),
    };
}
